import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Printer, Search, ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const buildStageMeta = (s) => ({
  stage_30: { label: `${s?.stage_1_discount ?? 30}% OFF`, cls: "bg-blue-600 text-white" },
  stage_40: { label: `${s?.stage_2_discount ?? 40}% OFF`, cls: "bg-orange-500 text-white" },
  oil_change: { label: "Free Oil", cls: "bg-sky-600 text-white" },
  parts_arrived: { label: "Parts Arrived", cls: "bg-amber-600 text-white" },
  csi: { label: "CSI", cls: "bg-emerald-600 text-white" },
  body_shop: { label: "Body Shop", cls: "bg-rose-600 text-white" },
  advisor_preflight: { label: "Advisor Prep", cls: "bg-navy text-white" },
  advisor_livecall: { label: "Live Call Alert", cls: "bg-cyan-700 text-white" },
  prospect: { label: "Win-Back", cls: "bg-purple-600 text-white" },
  fast_oil_50: { label: "50% Oil", cls: "bg-teal-600 text-white" },
  fast_tire_rotation: { label: "Free Rotation", cls: "bg-teal-700 text-white" },
  fast_wipers_50: { label: "50% Wipers", cls: "bg-indigo-600 text-white" },
  fast_tire_special: { label: "Tire Special", cls: "bg-fuchsia-600 text-white" },
  fast_detail_50: { label: "50% Detail", cls: "bg-pink-600 text-white" },
  fast_referral: { label: "Send-a-Friend", cls: "bg-lime-700 text-white" },
  fast_test_drive_1000: { label: "$1K Test Drive", cls: "bg-[hsl(var(--gold))] text-neutral-900" },
  fast_new_car_detail: { label: "$100 Detail", cls: "bg-slate-700 text-white" },
  fast_ppf_wrap_45: { label: "45% PPF Wrap", cls: "bg-cyan-700 text-white" },
  final_chance: { label: "Final Chance", cls: "bg-red-700 text-white" },
});

// Coupon border color for each stage — matches the Badge color so an advisor
// can eyeball the SMS log and know the campaign at a glance without reading.
const buildStageBorder = (s) => ({
  stage_30: "border-l-4 border-l-blue-600",
  stage_40: "border-l-4 border-l-orange-500",
  oil_change: "border-l-4 border-l-sky-600",
  parts_arrived: "border-l-4 border-l-amber-600",
  csi: "border-l-4 border-l-emerald-600",
  body_shop: "border-l-4 border-l-rose-600",
  advisor_preflight: "border-l-4 border-l-navy",
  advisor_livecall: "border-l-4 border-l-cyan-700",
  prospect: "border-l-4 border-l-purple-600",
  fast_oil_50: "border-l-4 border-l-teal-600",
  fast_tire_rotation: "border-l-4 border-l-teal-700",
  fast_wipers_50: "border-l-4 border-l-indigo-600",
  fast_tire_special: "border-l-4 border-l-fuchsia-600",
  fast_detail_50: "border-l-4 border-l-pink-600",
  fast_referral: "border-l-4 border-l-lime-700",
  fast_test_drive_1000: "border-l-4 border-l-[hsl(var(--gold))]",
  fast_new_car_detail: "border-l-4 border-l-slate-700",
  fast_ppf_wrap_45: "border-l-4 border-l-cyan-700",
  final_chance: "border-l-4 border-l-red-700",
});

// What the coupon is worth — surfaced on the log so James can eyeball
// dollars-out at a glance.
const STAGE_PRICE = {
  stage_30: "30% OFF",
  stage_40: "40% OFF",
  oil_change: "FREE",
  fast_oil_50: "50% OFF",
  fast_tire_rotation: "FREE",
  fast_wipers_50: "50% OFF",
  fast_tire_special: "SPECIAL",
  fast_detail_50: "50% OFF",
  fast_referral: "$50 OFF",
  fast_test_drive_1000: "$1,000",
  fast_new_car_detail: "$100",
  fast_ppf_wrap_45: "45% OFF",
  final_chance: "LAST CALL",
};

export default function SmsLogPage() {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [settings, setSettings] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [feed, setFeed] = useState("all"); // all | customer | team
  const [searchParams, setSearchParams] = useSearchParams();
  const STAGE_META = useMemo(() => buildStageMeta(settings), [settings]);
  const STAGE_BORDER = useMemo(() => buildStageBorder(settings), [settings]);

  const openThread = async (phone) => {
    if (!phone) return;
    setThreadLoading(true);
    setThread({ phone, messages: [], recipient_label: phone });
    setReply("");
    try {
      const { data } = await api.get(`/sms/thread?phone=${encodeURIComponent(phone)}`);
      setThread(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setThread(null);
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([api.get("/sms"), api.get("/settings")])
      .then(([m, s]) => {
        setMessages(m.data);
        setSettings(s.data);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, []);

  // Deep-link support: /sms?thread=+15551110004 opens that thread automatically.
  useEffect(() => {
    const phone = searchParams.get("thread");
    if (phone && !thread) {
      openThread(phone);
      // Clean the URL so refresh doesn't re-open
      searchParams.delete("thread");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const sendReply = async () => {
    if (!reply.trim() || !thread?.phone) return;
    setSendingReply(true);
    try {
      await api.post(`/sms/thread/reply`, {
        phone: thread.phone,
        body: reply.trim(),
        recipient_label: thread.recipient_label,
      });
      toast.success("Reply logged");
      setReply("");
      const { data } = await api.get(`/sms/thread?phone=${encodeURIComponent(thread.phone)}`);
      setThread(data);
      // Also refresh the main /sms list so the reply shows up there
      const list = await api.get("/sms");
      setMessages(list.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSendingReply(false);
    }
  };

  // Every campaign_type generated by Posse workflows — used to separate
  // internal "team comms" from customer-facing campaigns in the SMS log.
  const TEAM_CAMPAIGNS = useMemo(
    () => new Set([
      "posse_master_text",
      "posse_light_toggle",
      "posse_photo_alert",
      "posse_photo_booking",
      "posse_flag",
      "posse_reply",
      "manual_reply",
    ]),
    []
  );

  const filtered = useMemo(() => {
    let list = messages;
    if (feed === "team") list = list.filter((m) => TEAM_CAMPAIGNS.has(m.campaign_type));
    if (feed === "customer") list = list.filter((m) => !TEAM_CAMPAIGNS.has(m.campaign_type));
    if (!query.trim()) return list;
    const q = query.trim().toLowerCase();
    return list.filter(
      (m) =>
        (m.customer_name || "").toLowerCase().includes(q) ||
        (m.recipient_name || "").toLowerCase().includes(q) ||
        (m.recipient_role || "").toLowerCase().includes(q) ||
        (m.to_phone || m.phone || "").includes(q) ||
        (m.body || "").toLowerCase().includes(q) ||
        (m.coupon_code || "").toLowerCase().includes(q)
    );
  }, [messages, query, feed, TEAM_CAMPAIGNS]);

  const hasMock = messages.some((m) => m.provider === "mock");

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Outbound SMS Log" />
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-label text-muted-foreground">Outbound</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">SMS Log</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Every follow-up text your dealership has sent — the customer name, the exact wording, the coupon
            code, and whether it went out or was queued.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap no-print">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, wording, coupon…"
              className="pl-9 w-72"
              data-testid="sms-search"
            />
          </div>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-sms-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      {/* What "mock" means — friendly explainer */}
      {hasMock && (
        <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 mb-6 no-print" data-testid="mock-explainer">
          <div className="text-xs text-amber-900 dark:text-amber-200">
            <b>What does &quot;mock&quot; mean?</b> The text is logged here (exact wording, coupon, timestamp)
            but did NOT actually go to the customer&apos;s phone. That&apos;s because Twilio isn&apos;t hooked
            up yet — great for demos, but no real SMS is sent. Paste your Twilio account keys in Settings and
            the badge flips to <b>twilio</b> and real texts start hitting phones.
          </div>
        </Card>
      )}

      <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2 flex-wrap no-print">
        <span>
          {filtered.length} of {messages.length} messages{query ? ` matching "${query}"` : ""}
        </span>
        <div className="ml-auto flex gap-1" data-testid="sms-feed-filter">
          {[
            { id: "all",      label: "All",               n: messages.length },
            { id: "customer", label: "Customer Campaigns", n: messages.filter((m) => !TEAM_CAMPAIGNS.has(m.campaign_type)).length },
            { id: "team",     label: "Team Comms",         n: messages.filter((m) => TEAM_CAMPAIGNS.has(m.campaign_type)).length },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFeed(f.id)}
              className={`rounded-full px-3 py-1 border text-[0.65rem] font-semibold tracking-wide transition-colors ${
                feed === f.id
                  ? "bg-navy text-white border-navy"
                  : "bg-background text-neutral-700 border-border hover:border-navy"
              }`}
              data-testid={`sms-feed-${f.id}`}
            >
              {f.label}
              <span className="ml-1 opacity-70 tabular-nums">{f.n}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.length === 0 && (
          <Card className="p-8 col-span-full text-center border-border">
            <MessageSquare className="w-6 h-6 mx-auto text-muted-foreground" strokeWidth={1.75} />
            <p className="text-sm text-muted-foreground mt-3">
              {messages.length === 0
                ? 'No messages sent yet. Import declines then click "Run Campaign Cycle".'
                : "No messages match your search."}
            </p>
          </Card>
        )}
        {filtered.map((m) => {
          const meta = STAGE_META[m.stage] || { label: m.stage, cls: "bg-neutral-500 text-white" };
          const borderCls = STAGE_BORDER[m.stage] || "";
          const price = STAGE_PRICE[m.stage];
          const isMock = m.provider === "mock";
          const phone = m.to_phone || m.phone;
          return (
            <Card key={m.id} className={`p-5 border-border ${borderCls}`} data-testid={`sms-${m.id}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="font-medium">{m.customer_name || m.recipient_name || m.recipient_role}</div>
                  <div className="text-xs text-muted-foreground">{phone}</div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Badge className={meta.cls}>{meta.label}</Badge>
                  {price && (
                    <span className="text-[0.7rem] font-mono-label font-bold text-navy bg-[hsl(var(--gold-light))]/40 border border-[hsl(var(--gold))] px-2 py-0.5 rounded-full" data-testid={`sms-price-${m.id}`}>
                      {price}
                    </span>
                  )}
                  <Badge
                    variant="outline"
                    className={isMock ? "border-amber-400 text-amber-700 dark:text-amber-300" : "border-emerald-500 text-emerald-700 dark:text-emerald-300"}
                    title={isMock ? "Logged only — did NOT hit the customer's phone (Twilio off)" : "Sent live via Twilio"}
                  >
                    {isMock ? "mock (not sent)" : "sent live"}
                  </Badge>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{m.body}</p>
              <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                <div className="font-mono-label text-muted-foreground">
                  {new Date(m.sent_at || m.created_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {m.coupon_code && (
                    <div className="font-mono text-[0.65rem] bg-muted px-2 py-0.5 rounded">
                      {m.coupon_code}
                    </div>
                  )}
                  {phone && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => openThread(phone)}
                      data-testid={`sms-view-thread-${m.id}`}
                    >
                      <MessageSquare className="w-3 h-3 mr-1" strokeWidth={2.2} />
                      View thread
                      <ArrowRight className="w-3 h-3 ml-1" strokeWidth={2.2} />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {filtered.length > 8 && (
        <div className="mt-6 text-center no-print">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="sms-scroll-top-btn"
          >
            ↑ Back to top
          </Button>
        </div>
      )}

      {/* Conversation thread modal — full SMS history for a single phone */}
      <Dialog open={!!thread} onOpenChange={(open) => !open && setThread(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="sms-thread-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-baseline gap-2 flex-wrap">
              <MessageSquare className="w-4 h-4" strokeWidth={2.2} />
              <span>Conversation</span>
              {thread?.recipient_label && (
                <span className="text-sm font-normal text-muted-foreground">
                  · {thread.recipient_label}
                </span>
              )}
              {thread?.count > 0 && (
                <span className="ml-auto text-xs font-mono-label tracking-widest text-muted-foreground">
                  {thread.count} MESSAGES
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground font-mono">{thread?.phone}</div>
          <div
            className="flex-1 overflow-y-auto space-y-2 py-2 pr-1"
            data-testid="sms-thread-body"
          >
            {threadLoading && (
              <div className="text-center text-sm text-muted-foreground py-8">
                Loading…
              </div>
            )}
            {!threadLoading && thread?.messages?.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8">
                No messages found for this number.
              </div>
            )}
            {(thread?.messages || []).map((m, i) => {
              const ts = m.sent_at || m.created_at;
              const meta = STAGE_META[m.stage];
              return (
                <div
                  key={m.id || i}
                  className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 p-3 max-w-[85%] ml-auto"
                  data-testid={`sms-thread-msg-${i}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap text-[0.6rem]">
                    {meta && <Badge className={meta.cls}>{meta.label}</Badge>}
                    {m.campaign_type && !meta && (
                      <Badge variant="outline" className="text-[0.55rem] tracking-widest">
                        {m.campaign_type.replace(/_/g, " ")}
                      </Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={
                        m.provider === "mock"
                          ? "border-amber-400 text-amber-700 text-[0.55rem]"
                          : "border-emerald-500 text-emerald-700 text-[0.55rem]"
                      }
                    >
                      {m.provider === "mock" ? "mock" : "sent"}
                    </Badge>
                    {m.sent_by && (
                      <span className="text-[0.55rem] italic text-muted-foreground">
                        by {m.sent_by}
                      </span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed text-neutral-800 dark:text-neutral-100">
                    {m.body}
                  </p>
                  <div className="font-mono-label text-[0.55rem] text-muted-foreground mt-1 text-right">
                    {ts ? new Date(ts).toLocaleString() : "—"}
                    {m.coupon_code && (
                      <span className="ml-2 font-mono bg-muted px-1 rounded">
                        {m.coupon_code}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply composer — type a follow-up right from the thread */}
          {thread?.phone && (
            <div
              className="border-t border-border pt-3 mt-1"
              data-testid="sms-thread-composer"
            >
              <div className="flex gap-2 items-end">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  placeholder="Type a reply… ⌘/Ctrl+Enter to send"
                  rows={2}
                  className="text-sm resize-none flex-1"
                  data-testid="sms-thread-reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={sendingReply || !reply.trim()}
                  className="h-10 shrink-0"
                  data-testid="sms-thread-reply-send"
                >
                  <Send className="w-4 h-4 mr-1.5" strokeWidth={2.2} />
                  {sendingReply ? "Sending…" : "Send"}
                </Button>
              </div>
              <div className="text-[0.55rem] text-muted-foreground italic mt-1">
                Mocked until Twilio Trust Hub clears — the reply logs to the thread as{" "}
                <b>manual_reply</b> right away.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
